import { Injectable } from "@angular/core";
import { HttpService } from "../app/utility/http-service";
import { AuthService } from "@auth0/auth0-angular";
import { BehaviorSubject, filter, firstValueFrom } from 'rxjs';
import { Guid } from "guid-typescript";
import { GridApiService } from "../app/components/wc-grid/services/grid-api.service";

@Injectable()
export class LoginService {
  public userContext?: UserContext;
  /**
   * Auth0 `sub` values already hydrated this page-load. `sub` is the stable
   * per-user identifier in the ID token and is the direct replacement for
   * MSAL's homeAccountId -- it keeps a second login as a different user from
   * reusing the first user's context.
   */
  private _loadedForSubject = new Set<string>();
  constructor(private auth: AuthService, private http: HttpService,
    private gridApi: GridApiService) {

  }

  private readonly _user$ = new BehaviorSubject<UserContext | null>(null);

  /** Observable for components / guards */
  user$ = this._user$.asObservable();

  /** Sync access when you already know it’s loaded */
  get user(): UserContext {
    if (!this._user$.value) {
      throw new Error(
        'UserContext accessed before user was hydrated. ' +
        'Ensure UserHydrationGuard or AuthInitService ran first.'
      );
    }
    return this._user$.value;
  }
  get userOptional(): (UserContext|undefined) {
    if (!this._user$.value) return undefined;
    return this._user$.value;
  }

  setUser(user: UserContext): void {
    this._user$.next(user);
  }

  clear(): void {
    this._user$.next(null);
  }

  async ensureUserLoaded(): Promise<void> {
    // Wait for the SDK to settle -- redirect callback processed and any cached
    // session restored -- before deciding whether anyone is signed in.
    await firstValueFrom(
      this.auth.isLoading$.pipe(filter(loading => !loading))
    );

    const profile = await firstValueFrom(this.auth.user$);

    if (!profile?.sub) return;

    if (this._loadedForSubject.has(profile.sub)) {
      return;
    }

    const user = await this.http.getWait<UserContext>("User/GetUserInfo");

    if (!user) {
      throw new Error('Backend returned no user for authenticated account');
    }
    this.setUser(user);
    this._loadedForSubject.add(profile.sub);
  }

  async reload(): Promise<void> {
    const user = await this.http.getWait<UserContext>("User/GetUserInfo");

    if (!user) {
      throw new Error('Backend returned no user for authenticated account');
    }
    // Grid definitions/lookups are shaped by the caller's role and company, so any
    // identity change (impersonate, exit impersonation) must drop the cached ones —
    // otherwise the next render can reuse data scoped to whoever loaded it first.
    this.gridApi.clearCache();
    this.setUser(user);
  }

  /** For guards / bootstrap checks only */
  get isLoaded(): boolean {
    return !!this._user$.value;
  }

}


export declare interface UserContext {
  defaultClientId: string;
  selectedClientId: string;
  iconText: string;
  width: number;
  height: number;
  isGlobalAdmin: boolean;
  isDeveloper: boolean;
  isCompanyAdmin: boolean;
  availableClients: UserClient[];
  isImpersonating: boolean;
  userName: string;
  firstName: string;
  lastName: string;
  enableFracX: boolean;
  isRequestSubmitted: boolean;
  isPending: boolean;
  defaultApplication?: string;
  enableFracXAutomation: boolean;
  enableFracXContacts: boolean;
  enableFracXContactsEdit: boolean;
  wdxUserName?: string;
  enableAquatrade: boolean;
  enableAquashare: boolean;
  dashboards: Dashboard[]
}

export declare interface Dashboard {
  dashboardId: Guid;
  name: string;
}

export declare interface UserClient {
  id: string;
  displayName: string;
  isPartner: boolean;
}
